/**
 * FieldBand — three-band visual hierarchy primitive for the rerun
 * Configuration step (Configuration Upgrade CU1).
 *
 * Wraps a group of preference fields with band-appropriate visual
 * treatment + header chip:
 *
 *   - `score-affecting` — warning-toned border, "Affects score" header
 *     chip, hover tooltip explaining that this band mutates the
 *     deterministic score (CU4 surfaces the per-field chip; the band
 *     header acts as the group label).
 *   - `recommended`     — default border, no chip. Rendered inline.
 *   - `optional`        — muted border, rendered inside `<details>`
 *     collapsed by default so the happy path stays clean. The chevron
 *     announces it as expandable; the header chip says "Optional".
 *
 * Pure presentation — no state, no business logic. The parent decides
 * which fields land in each band via `PREFS_BAND_MAP` (CU1).
 */
import React from 'react';

export type FieldBandKind = 'score-affecting' | 'recommended' | 'optional';

export interface FieldBandProps {
  band: FieldBandKind;
  /** Header label, e.g. "Score-affecting", "Recommended", "Optional". */
  title?: string;
  /** Optional sub-caption rendered under the title. */
  caption?: string;
  /** Optional override for the default collapsed state on `optional`. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const BAND_TITLES: Record<FieldBandKind, string> = {
  'score-affecting': 'Score-affecting',
  recommended: 'Recommended',
  optional: 'Optional',
};

const BAND_CAPTIONS: Record<FieldBandKind, string> = {
  'score-affecting': 'Changes here move the deterministic score.',
  recommended: 'Strong narrative impact — set these for best results.',
  optional: 'Frame the narrative; leave blank if unsure.',
};

const BAND_CHIP: Record<FieldBandKind, { label: string; className: string }> = {
  'score-affecting': {
    label: 'Affects score',
    className: 'text-warning border border-warning/40 bg-warning/5',
  },
  recommended: {
    label: 'Recommended',
    className: 'text-primary border border-primary/30 bg-primary/5',
  },
  optional: {
    label: 'Optional',
    className: 'text-text-muted border border-border-subtle bg-transparent',
  },
};

const BAND_OUTER: Record<FieldBandKind, string> = {
  'score-affecting': 'border-l-2 border-warning/60 pl-3',
  recommended: 'border-l border-border pl-3',
  optional: 'border-l border-border-subtle pl-3',
};

export const FieldBand: React.FC<FieldBandProps> = ({
  band,
  title,
  caption,
  defaultOpen,
  children,
}) => {
  const resolvedTitle = title ?? BAND_TITLES[band];
  const resolvedCaption = caption ?? BAND_CAPTIONS[band];
  const chip = BAND_CHIP[band];

  const header = (
    <div className="flex items-center gap-2 mb-2">
      <span
        className={`inline-flex items-center type-meta uppercase tracking-widest px-1.5 py-0.5 ${chip.className}`}
        data-testid={`field-band-chip-${band}`}
      >
        {chip.label}
      </span>
      <div className="flex-1">
        <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
          {resolvedTitle}
        </div>
        {resolvedCaption && (
          <div className="type-meta text-text-muted mt-0.5">{resolvedCaption}</div>
        )}
      </div>
    </div>
  );

  // Optional band collapses by default into a <details> disclosure so
  // the happy path stays uncluttered. score-affecting + recommended
  // always render inline so the user sees them immediately.
  if (band === 'optional') {
    return (
      <details
        className={`group ${BAND_OUTER[band]}`}
        open={defaultOpen ?? false}
        data-testid={`field-band-${band}`}
      >
        <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`inline-flex items-center type-meta uppercase tracking-widest px-1.5 py-0.5 ${chip.className}`}
              data-testid={`field-band-chip-${band}`}
            >
              {chip.label}
            </span>
            <div className="flex-1">
              <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
                {resolvedTitle}
              </div>
              {resolvedCaption && (
                <div className="type-meta text-text-muted mt-0.5">{resolvedCaption}</div>
              )}
            </div>
            <span
              className="type-meta uppercase tracking-widest text-text-muted group-open:hidden"
              aria-hidden="true"
            >
              Show
            </span>
            <span
              className="type-meta uppercase tracking-widest text-text-muted hidden group-open:inline"
              aria-hidden="true"
            >
              Hide
            </span>
          </div>
        </summary>
        <div className="space-y-2">{children}</div>
      </details>
    );
  }

  return (
    <div className={BAND_OUTER[band]} data-testid={`field-band-${band}`}>
      {header}
      <div className="space-y-2">{children}</div>
    </div>
  );
};
